import type { AccountUpdateRequest } from '@aloft/toolkit-shared';
import { ACCOUNT_API } from '@aloft/toolkit-shared';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

import type { UpdateAccountError } from '../../hooks/api/toolkitComponents.ts';
import { fetchUpdateAccount } from '../../hooks/api/toolkitComponents.ts';
import type { ToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { apiAccountToToolkitAccount } from '../../utils/apiAccountToToolkitAccount.ts';
import type { ToolkitAccount } from '../types.ts';

type ToolkitAccountUpdateVariables = Pick<
  ToolkitAccount,
  | 'address'
  | 'company'
  | 'firstName'
  | 'lastName'
  | 'mlsIDs'
  | 'phone'
  | 'userDefinedRole'
  | 'isArchived'
  | 'licenseID'
  | 'licenseState'
  | 'preferredReportSoftware'
  | 'isOnboardingComplete'
  | 'referredBy'
>;

/**
 * Updates an instance of `Account`.
 */
export const useUpdateToolkitAccount = (
  options?: Omit<
    UseMutationOptions<ToolkitAccount, UpdateAccountError, ToolkitAccountUpdateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return useMutation<ToolkitAccount, UpdateAccountError, ToolkitAccountUpdateVariables>({
    mutationFn: (tkAccount) => updateAccount({ ...fetcherOptions, body: tkAccount }),
    ...options,
  });
};

const updateAccount = async (
  variables: { body: ToolkitAccountUpdateVariables } & ToolkitContext['fetcherOptions'],
  signal?: AbortSignal,
): Promise<ToolkitAccount> => {
  const { body } = variables;
  const request = toolkitAccountToAccountUpdateRequest(body);
  const validatedRequest = ACCOUNT_API.PUT.REQUEST.BODY.parse(request);
  const response = await fetchUpdateAccount(
    {
      ...variables,
      body: validatedRequest,
    },
    signal,
  );

  const validatedResponse = ACCOUNT_API.PUT.RESPONSE.parse(response);

  return apiAccountToToolkitAccount(validatedResponse);
};

const toolkitAccountToAccountUpdateRequest = (
  tkAccount: ToolkitAccountUpdateVariables,
): AccountUpdateRequest => {
  const {
    address,
    company,
    firstName,
    lastName,
    mlsIDs,
    phone,
    userDefinedRole,
    isArchived,
    licenseID,
    licenseState,
    preferredReportSoftware,
    isOnboardingComplete,
    referredBy,
  } = tkAccount;

  return {
    isArchived,
    body: {
      version: 1,
      address,
      company,
      firstName,
      lastName,
      mlsIDs,
      phone,
      userDefinedRole,
      license: {
        id: licenseID,
        state: licenseState,
      },
      preferredReportSoftware,
      isOnboardingComplete,
      referredBy,
    },
  };
};
